import { useEffect, useState, useCallback } from 'react';
import { UserPlus, UserCheck, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import FollowersModal from './FollowersModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Renders a Follow / Following toggle for a target user, plus clickable
 *  follower/following counts that open a list modal with mini avatars and
 *  links to each user's profile. Hidden if the viewer is the same person. */
export default function FollowButton({ targetUserId, viewerUserId, compact = false }) {
  const [stats, setStats] = useState({ followers_count: 0, following_count: 0, is_following: false, follows_viewer: false });
  const [loading, setLoading] = useState(false);
  const [modalTab, setModalTab] = useState(null); // 'followers' | 'following' | null

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const load = useCallback(async () => {
    if (!targetUserId) return;
    try {
      const res = await fetch(`${API}/social/users/${targetUserId}/social`, { headers: viewerUserId ? headers() : {} });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, [targetUserId, viewerUserId, headers]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!viewerUserId) return toast.error('Sign in to follow');
    setLoading(true);
    // Optimistic
    setStats((s) => ({
      ...s,
      is_following: !s.is_following,
      followers_count: s.is_following ? s.followers_count - 1 : s.followers_count + 1,
    }));
    try {
      const res = await fetch(`${API}/social/follow/${targetUserId}`, { method: 'POST', headers: headers() });
      if (!res.ok) {
        // rollback
        setStats((s) => ({
          ...s,
          is_following: !s.is_following,
          followers_count: s.is_following ? s.followers_count - 1 : s.followers_count + 1,
        }));
        toast.error('Could not update follow');
      } else {
        const d = await res.json();
        setStats((s) => ({ ...s, is_following: d.is_following }));
      }
    } finally { setLoading(false); }
  };

  if (viewerUserId === targetUserId) {
    return (
      <span className="text-xs text-zinc-300 font-medium" data-testid="follow-self">
        {stats.followers_count} followers · {stats.following_count} following
      </span>
    );
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {stats.follows_viewer && (
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200 text-[10px] font-bold uppercase tracking-wider"
          data-testid={`follows-you-${targetUserId}`}
          title="This user follows you"
        >
          <UserCheck size={11} /> Follows you
        </span>
      )}
      <button
        onClick={toggle}
        disabled={loading}
        className={`flex items-center gap-1.5 ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-full font-bold transition-all disabled:opacity-60 ${
          stats.is_following
            ? 'bg-white/10 text-white border border-white/20 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-200'
            : 'bg-yellow-400 text-black hover:bg-yellow-300 shadow-[0_4px_18px_-4px_rgba(250,204,21,0.5)]'
        }`}
        data-testid={`follow-btn-${targetUserId}`}
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : stats.is_following ? <UserCheck size={13} /> : <UserPlus size={13} />}
        {stats.is_following ? 'Following' : stats.follows_viewer ? 'Follow back' : 'Follow'}
      </button>
      <button
        type="button"
        onClick={() => setModalTab('followers')}
        className="text-xs text-zinc-300 hover:text-white inline-flex items-center gap-1 rounded-full px-2 py-1 hover:bg-white/5"
        data-testid={`follower-count-${targetUserId}`}
      >
        <Users size={11} className="text-fuchsia-300" />
        <span className="text-white font-semibold">{stats.followers_count}</span> followers
      </button>
      <button
        type="button"
        onClick={() => setModalTab('following')}
        className="text-xs text-zinc-300 hover:text-white inline-flex items-center gap-1 rounded-full px-2 py-1 hover:bg-white/5"
        data-testid={`following-count-${targetUserId}`}
      >
        <span className="text-white font-semibold">{stats.following_count}</span> following
      </button>
      <FollowersModal
        open={modalTab !== null}
        onClose={() => setModalTab(null)}
        userId={targetUserId}
        initialTab={modalTab || 'followers'}
        followersCount={stats.followers_count}
        followingCount={stats.following_count}
      />
    </div>
  );
}
